// hooks/useStrangerDetectionLogic.js
import { useState, useEffect, useMemo } from 'react';
import employeeApi from 'services/api/employeeApi';
import strangerApi from 'services/api/strangerApi';

const useStrangerDetectionLogic = () => {
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);
  const [selectedStrangers, setSelectedStrangers] = useState([]);
  const [detailsModal, setDetailsModal] = useState({ isOpen: false, stranger: null });
  const [switchModal, setSwitchModal] = useState({ isOpen: false, stranger: null });
  const [timeFilter, setTimeFilter] = useState('today'); // 'today', 'week', 'month', 'year'
  const [attendanceModal, setAttendanceModal] = useState({
    isOpen: false,
    stranger: null,
    attendanceSummary: null,
    attendanceRecords: [],
    timeFilter: 'today'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [sortBy, setSortBy] = useState('detectedAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [strangers, setStrangers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [cameras, setCameras] = useState([]);

  // New state for detection class tabs
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'person', 'mask', 'helmet'
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'gallery'

  // New state for detection counts
  const [detectionCounts, setDetectionCounts] = useState({
    person: 0,
    mask: 0,
    helmet: 0,
    gloves: 0,
  });

  // Stats data
  const [stats, setStats] = useState({
    totalDetections: 0,
    newToday: 0,
  });

  // Filter state
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    status: 'all',
    location: 'All Locations',
    confidenceMin: 0,
    confidenceMax: 100,
    search: ''
  });

  // Notification functions
  const closeNotification = () => {
    setNotification(null);
  };

  const showNotification = (message, type = "info") => {
    setNotification({ message, type });
  };

  // Load cameras from API
  const loadCameras = async () => {
    try {
      const camerasData = await strangerApi.getCameras();
      setCameras(camerasData);
    } catch (error) {
      console.error("Error fetching cameras:", error);
      showNotification("Failed to fetch cameras", "error");
    }
  };

  // Load strangers from API
  const loadStrangers = async () => {
    try {
      setLoading(true);
      const allEmployees = await strangerApi.getEmployees();

      // Filter to only include strangers (unknown individuals)
      const strangerData = allEmployees
        .filter(emp => emp?.isUnkown)
        .map(emp => {
          // Extract detection class from captured image path
          let detectionClass = 'person'; // Default

          if (emp.image1) {
            const imagePath = emp.image1.toLowerCase(); // make case-insensitive
            console.log("img", imagePath)
            if (imagePath.includes('\\mask') || imagePath.includes('/mask/')) {
              detectionClass = 'mask';
            } else if (imagePath.includes('\\hardhat') || imagePath.includes('/hardhat/')) {
              detectionClass = 'helmet';
            } else if (imagePath.includes('\\person') || imagePath.includes('/person/')) {
              detectionClass = 'person';
            }
            else if (imagePath.includes('\\gloves') || imagePath.includes('/gloves/')) {
              detectionClass = 'gloves';
            }
          }

          return {
            id: emp?.employee_id || `STR-${emp?.id}`,
            first_name: emp?.first_name,
            email: emp?.email,
            department: emp?.department,
            date_of_join: emp?.date_of_join,
            last_name: emp?.last_name,
            imageUrl: emp?.image1 || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face',
            location: emp?.cameraName || 'Unknown',
            cameraName: emp?.cameraName || 'CAM-000',
            confidence: emp?.confidence || 85,
            detectedAt: emp?.detectedAt ? new Date(emp.detectedAt) : new Date(),
            status: emp?.status || 'new',
            detectionCount: emp?.detection_count || 1,
            notes: emp?.notes || '',
            detectionClass: detectionClass, // Set detection class from captured image
            // Include original employee data for reference
            originalData: emp
          };
        });

      setStrangers(strangerData);

      // Calculate detection counts
      const counts = {
        person: 0,
        mask: 0,
        helmet: 0,
        gloves: 0,
      };

      strangerData.forEach(stranger => {
        if (stranger.detectionClass === 'person') counts.person++;
        else if (stranger.detectionClass === 'mask') counts.mask++;
        else if (stranger.detectionClass === 'helmet') counts.helmet++;
        else if (stranger.detectionClass === 'gloves') counts.gloves++;
      });

      setDetectionCounts(counts);

      // Calculate stats
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const newToday = strangerData.filter(stranger => {
        const detectedDate = new Date(stranger.detectedAt);
        detectedDate.setHours(0, 0, 0, 0);
        return detectedDate.getTime() === today.getTime();
      }).length;

      setStats({
        totalDetections: strangerData.length,
        newToday: newToday
      });
    } catch (error) {
      console.error("Error fetching strangers:", error);
      showNotification("Failed to fetch stranger detections", "error");
    } finally {
      setLoading(false);
    }
  };

  // Fetch employee attendance summary
  const fetchEmployeeAttendanceSummary = async (employeeId) => {
    try {
      const summary = await strangerApi.getEmployeeAttendanceSummary(employeeId);
      return summary;
    } catch (err) {
      console.error("Error fetching employee attendance summary:", err);
      showNotification("Failed to fetch employee attendance summary", "error");
      return null;
    }
  };

  // Fetch employee attendance records
  const fetchEmployeeAttendanceRecords = async (employeeId, filter) => {
    try {
      const records = await strangerApi.getEmployeeAttendanceRecords(employeeId, filter);
      return records;
    } catch (err) {
      console.error("Error fetching employee attendance records:", err);
      showNotification("Failed to fetch employee attendance records", "error");
      return [];
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadCameras();
    loadStrangers();
  }, []);

  // Handle time filter change
  const handleTimeFilterChange = (filter) => {
    setTimeFilter(filter);

    // Calculate date range based on filter
    const today = new Date();
    let dateFrom = '';
    let dateTo = '';

    switch (filter) {
      case 'today':
        dateFrom = today.toISOString().split('T')[0];
        dateTo = today.toISOString().split('T')[0];
        break;
      case 'week':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        dateFrom = startOfWeek.toISOString().split('T')[0];
        dateTo = today.toISOString().split('T')[0];
        break;
      case 'month':
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        dateFrom = startOfMonth.toISOString().split('T')[0];
        dateTo = today.toISOString().split('T')[0];
        break;
      case 'year':
        const startOfYear = new Date(today.getFullYear(), 0, 1);
        dateFrom = startOfYear.toISOString().split('T')[0];
        dateTo = today.toISOString().split('T')[0];
        break;
      default:
        break;
    }

    // Update filters state with new date range
    setFilters(prev => ({
      ...prev,
      dateFrom,
      dateTo
    }));
  };

  // Filter and sort strangers
  const filteredStrangers = useMemo(() => {
    let filtered = strangers?.filter(stranger => {
      // Filter by detection class if activeTab is not 'all'
      if (activeTab !== 'all' && stranger?.detectionClass !== activeTab) return false;

      // Status filter
      if (filters?.status !== 'all' && stranger?.status !== filters?.status) return false;

      // Location filter
      if (filters?.location !== 'All Locations' && stranger?.location !== filters?.location) return false;

      // Confidence filter
      if (stranger?.confidence < filters?.confidenceMin || stranger?.confidence > filters?.confidenceMax) return false;

      // Search filter
      if (filters?.search && !stranger?.location?.toLowerCase()?.includes(filters?.search?.toLowerCase()) &&
        !stranger?.cameraName?.toLowerCase()?.includes(filters?.search?.toLowerCase()) &&
        !stranger?.id?.toLowerCase()?.includes(filters?.search?.toLowerCase())) return false;

      // Date filters
      const detectedDate = new Date(stranger.detectedAt);

      // For dateFrom: we want detections on or after this date
      if (filters?.dateFrom) {
        const fromDate = new Date(filters.dateFrom);
        fromDate.setHours(0, 0, 0); // Set to start of day
        if (detectedDate < fromDate) return false;
      }

      // For dateTo: we want detections on or before this date
      if (filters?.dateTo) {
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999); // Set to end of day
        if (detectedDate > toDate) return false;
      }

      return true;
    }) || [];

    // Sort filtered results
    return filtered?.sort((a, b) => {
      const aValue = a?.[sortBy];
      const bValue = b?.[sortBy];

      // Handle date sorting
      if (sortBy === 'detectedAt') {
        const aDate = new Date(aValue).getTime();
        const bDate = new Date(bValue).getTime();
        return sortOrder === 'asc' ? aDate - bDate : bDate - aDate;
      }

      // Handle other sorting
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      }
      return aValue < bValue ? 1 : -1;
    });
  }, [strangers, filters, sortBy, sortOrder, activeTab]);

  // Pagination
  const totalPages = Math.ceil(filteredStrangers?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  // const paginatedStrangers = useMemo(() => {
  //   console.log("paginatedStrangers",filteredStrangers)
  //   return filteredStrangers?.slice(startIndex, startIndex + itemsPerPage);
  // }, [filteredStrangers, startIndex, itemsPerPage]);
  const paginatedStrangers = useMemo(() => {
    if (!filteredStrangers) return [];

    const sorted = [...filteredStrangers].sort((a, b) => {
      const dateA = a.detectedAt ? new Date(a.detectedAt) : 0;
      const dateB = b.detectedAt ? new Date(b.detectedAt) : 0;
      return dateB - dateA;
    });

    return sorted.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredStrangers, startIndex, itemsPerPage]);


  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, activeTab]);

  // Handlers
  const handleStrangerSelect = (strangerId, isSelected) => {
    if (isSelected) {
      setSelectedStrangers([...selectedStrangers, strangerId]);
    } else {
      setSelectedStrangers(selectedStrangers?.filter(id => id !== strangerId));
    }
  };

  const handleSelectAll = () => {
    if (selectedStrangers?.length === paginatedStrangers?.length) {
      setSelectedStrangers([]);
    } else {
      setSelectedStrangers(paginatedStrangers?.map(s => s?.id));
    }
  };

  const handleIdentify = async (status) => {
    try {
      // Extract employee ID from stranger ID
      if (status) {
        showNotification("Stranger identified successfully", "success");
      }
    } catch (error) {
      console.error("Error identifying stranger:", error);
      showNotification("Failed to identify stranger", "error");
    }
  };

  const handleMarkAsVisitor = async (strangerId) => {
    try {
      await strangerApi.updateStrangerStatus(strangerId, 'visitor');

      // Update local state
      setStrangers(prev => prev.map(stranger =>
        stranger.id === strangerId
          ? { ...stranger, status: 'visitor' }
          : stranger
      ));

      showNotification("Stranger marked as visitor", "success");
    } catch (error) {
      console.error("Error marking as visitor:", error);
      showNotification("Failed to mark as visitor", "error");
    }
  };

  const handleDismiss = async (strangerId) => {
    try {
      await strangerApi.updateStrangerStatus(strangerId, 'inactive');

      // Update local state
      setStrangers(prev => prev.map(stranger =>
        stranger.id === strangerId
          ? { ...stranger, status: 'inactive' }
          : stranger
      ));

      showNotification("Stranger dismissed", "success");
    } catch (error) {
      console.error("Error dismissing stranger:", error);
      showNotification("Failed to dismiss stranger", "error");
    }
  };

  const handleActivate = async (strangerId) => {
    try {
      await strangerApi.updateStrangerStatus(strangerId, 'active');

      // Update local state
      setStrangers(prev => prev.map(stranger =>
        stranger.id === strangerId
          ? { ...stranger, status: 'active' }
          : stranger
      ));

      showNotification("Stranger activated", "success");
    } catch (error) {
      console.error("Error activating stranger:", error);
      showNotification("Failed to activate stranger", "error");
    }
  };

  const handleBulkIdentify = async () => {
    try {
      await strangerApi.bulkUpdateStrangerStatus(selectedStrangers, 'identified');

      // Update local state
      setStrangers(prev => prev.map(stranger =>
        selectedStrangers.includes(stranger.id)
          ? { ...stranger, status: 'identified' }
          : stranger
      ));

      setSelectedStrangers([]);
      showNotification(`${selectedStrangers.length} strangers identified`, "success");
    } catch (error) {
      console.error("Error in bulk identify:", error);
      showNotification("Failed to identify strangers", "error");
    }
  };

  const handleBulkMarkAsVisitor = async () => {
    try {
      await strangerApi.bulkUpdateStrangerStatus(selectedStrangers, 'visitor');

      // Update local state
      setStrangers(prev => prev.map(stranger =>
        selectedStrangers.includes(stranger.id)
          ? { ...stranger, status: 'visitor' }
          : stranger
      ));

      setSelectedStrangers([]);
      showNotification(`${selectedStrangers.length} strangers marked as visitors`, "success");
    } catch (error) {
      console.error("Error in bulk mark as visitor:", error);
      showNotification("Failed to mark as visitors", "error");
    }
  };

  const handleBulkDismiss = async () => {
    try {
      await strangerApi.bulkUpdateStrangerStatus(selectedStrangers, 'inactive');

      // Update local state
      setStrangers(prev => prev.map(stranger =>
        selectedStrangers.includes(stranger.id)
          ? { ...stranger, status: 'inactive' }
          : stranger
      ));

      setSelectedStrangers([]);
      showNotification(`${selectedStrangers.length} strangers dismissed`, "success");
    } catch (error) {
      console.error("Error in bulk dismiss:", error);
      showNotification("Failed to dismiss strangers", "error");
    }
  };

  const handleBulkActivate = async () => {
    try {
      await strangerApi.bulkUpdateStrangerStatus(selectedStrangers, 'active');

      // Update local state
      setStrangers(prev => prev.map(stranger =>
        selectedStrangers.includes(stranger.id)
          ? { ...stranger, status: 'active' }
          : stranger
      ));

      setSelectedStrangers([]);
      showNotification(`${selectedStrangers.length} strangers activated`, "success");
    } catch (error) {
      console.error("Error in bulk activate:", error);
      showNotification("Failed to activate strangers", "error");
    }
  };

  const handleBulkExport = () => {
    console.log('Exporting selected strangers:', selectedStrangers);
    // Generate CSV or PDF export
    showNotification("Export functionality would be implemented here", "info");
  };

  const handleViewDetails = (stranger) => {
    setDetailsModal({ isOpen: true, stranger });
  };

  const handleViewAttendance = async (stranger) => {
    try {
      // Get employee ID from stranger
      const employeeId = stranger.originalData.id;

      // Fetch attendance summary
      const summary = await fetchEmployeeAttendanceSummary(employeeId);

      // Fetch attendance records for today
      const records = await fetchEmployeeAttendanceRecords(employeeId, timeFilter);

      // Set attendance modal state with fetched data
      setAttendanceModal({
        isOpen: true,
        stranger,
        attendanceSummary: summary,
        attendanceRecords: records,
        timeFilter: 'today'
      });

    } catch (error) {
      console.error("Error viewing attendance:", error);
      showNotification("Failed to load attendance data", "error");
    }
  };

  const handleAttendanceTimeFilterChange = async (filter) => {
    if (!attendanceModal.stranger) return;

    try {
      // Get employee ID from stranger
      const employeeId = attendanceModal.stranger.originalData.id;

      // Fetch attendance records for selected filter
      const records = await fetchEmployeeAttendanceRecords(employeeId, filter);

      // Update attendance modal state with new records
      setAttendanceModal(prev => ({
        ...prev,
        attendanceRecords: records,
        timeFilter: filter
      }));
    } catch (error) {
      console.error("Error changing time filter:", error);
      showNotification("Failed to load attendance data", "error");
    }
  };

  const handleAddNotes = async (strangerId, notes) => {
    try {
      console.log('Adding notes to stranger:', strangerId, notes);
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Update local state
      setStrangers(prev => prev.map(stranger =>
        stranger.id === strangerId
          ? { ...stranger, notes }
          : stranger
      ));

      showNotification("Notes added successfully", "success");
    } catch (error) {
      console.error("Error adding notes:", error);
      showNotification("Failed to add notes", "error");
    }
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleResetFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      status: 'all',
      location: 'All Locations',
      confidenceMin: 0,
      confidenceMax: 100,
      search: ''
    });
    setTimeFilter('today'); // Reset time filter to today
    setCurrentPage(1);
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };
  const handleBulkDelete = async () => {
    const employeeIds = selectedStrangers.map(id => {
      return id.startsWith('STR-')
        ? (id.substring(4))
        : (id);
    });
    await employeeApi.deleteEmployees(employeeIds);
    setSelectedStrangers([]);
    loadStrangers()

    showNotification(`${employeeIds?.length} employee(s) deleted successfully`, "success");
  }

  const handleSwitchStranger = async (data) => {
    try {
      await strangerApi.switchStrangerToEmployee(data);
      showNotification("Stranger switched and attendance marked successfully", "success");
      loadStrangers(); // Refresh the list
    } catch (error) {
      console.error("Error switching stranger:", error);
      showNotification("Failed to switch stranger", "error");
    }
  };
  return {
    // State
    isNavCollapsed,
    filtersCollapsed,
    selectedStrangers,
    detailsModal,
    timeFilter,
    attendanceModal,
    currentPage,
    itemsPerPage,
    sortBy,
    sortOrder,
    strangers,
    loading,
    notification,
    cameras,
    activeTab,
    viewMode,
    detectionCounts,
    stats,
    filters,
    filteredStrangers,
    paginatedStrangers,
    totalPages,
    startIndex,

    // State setters
    setIsNavCollapsed,
    setFiltersCollapsed,
    setSelectedStrangers,
    setDetailsModal,
    setTimeFilter,
    setAttendanceModal,
    setCurrentPage,
    setSortBy,
    setSortOrder,
    setActiveTab,
    setViewMode,
    setFilters,

    // Functions
    closeNotification,
    showNotification,
    loadCameras,
    loadStrangers,
    fetchEmployeeAttendanceSummary,
    fetchEmployeeAttendanceRecords,
    handleTimeFilterChange,
    handleStrangerSelect,
    handleSelectAll,
    handleIdentify,
    handleMarkAsVisitor,
    handleDismiss,
    handleActivate,
    handleBulkIdentify,
    handleBulkMarkAsVisitor,
    handleBulkDismiss,
    handleBulkActivate,
    handleBulkExport,
    handleViewDetails,
    handleViewAttendance,
    handleAttendanceTimeFilterChange,
    handleAddNotes,
    handleFiltersChange,
    handleResetFilters,
    handleSortChange,
    handleBulkDelete,
    switchModal,
    setSwitchModal,
    handleSwitchStranger,
  };
};

export default useStrangerDetectionLogic;